import React, { useState, useEffect } from "react";
import "./Login.css";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import { HiEye, HiEyeOff } from "react-icons/hi";
import ForgetPassword from "../../Components/ForgetPassword/ForgetPassword";
import Strings from "../../utils/Strings";
import MessageBar, {
  config,
  errorNotify,
  successNotify,
} from "../../utils/helper";
import Spinner from "../../Components/Spinner/Spinner";
import { post } from "../../Config/network";
import configuration from "../../Config/configuration";
import { RiAdminFill } from "react-icons/ri";

const validate = (values) => {
  const errors = {};
  if (!values.primaryEmail) {
    errors.primaryEmail = "*Required";
  } else if (
    !/^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/.test(values.primaryEmail)
  ) {
    errors.primaryEmail = "*Invalid Email";
  }
  if (!values.password) {
    errors.password = "*Required";
  } else if (values.password.length < 6) {
    errors.password = "*Password must be at least 6 characters";
  }
  return errors;
};

const Login = () => {
  const isRemembered  = localStorage.getItem("rememberMe") === "true";
  const savedEmail    = isRemembered ? (localStorage.getItem("savedEmail")    || "") : "";
  const savedPassword = isRemembered ? (localStorage.getItem("savedPassword") || "") : "";

  const [forgetPasswordPopup, setForgetPasswordPopup] = useState(false);
  const [showPassword, setShowPassword]               = useState(false);
  const navigate                                       = useNavigate();
  const [isProcessing, setIsProcessing]               = useState(false);
  const [path, setPath]                               = useState("");
  const [isPasswordExpired, setIsPasswordExpired]     = useState(false);
  const [rememberMe, setRememberMe]                   = useState(false); // ✅ checkbox always unchecked on load

  const handleShowPassword   = () => setShowPassword(!showPassword);
  const handleForgetPassword = () => setForgetPasswordPopup(true);

  useEffect(() => {
    setPath(window.location.pathname);
  }, [window.location.pathname]);

  const formik = useFormik({
    initialValues: {
      primaryEmail: savedEmail,
      password:     savedPassword,
    },
    validate,
    onSubmit: (values) => {
      if (!isProcessing) handleSubmit(values);
    },
  });

  const handleRememberMeChange = (e) => {
    const checked = e.target.checked;
    setRememberMe(checked);
    
  };

  const handleSubmit = async (values) => {
    setIsProcessing(true);
    const data      = { primaryEmail: values.primaryEmail, password: values.password };
    const adminData = { email: values.primaryEmail,        password: values.password };

    try {
      let res;
      if (path === "/admin") {
        res = await post(configuration.apis.adminLogin, adminData, config);
      } else {
        res = await post(configuration.apis.login, data, config);
      }

      const response = res?.data;

      if (response?.status) {
        if (response?.isPassword === false) {
          setForgetPasswordPopup(true);
          setIsPasswordExpired(true);
          setIsProcessing(false);
          return;
        }

        if (rememberMe) {
          localStorage.setItem("savedEmail",    values.primaryEmail);
          localStorage.setItem("savedPassword", values.password);
          localStorage.setItem("rememberMe",    "true");
        }

        const userData = path === "/admin" ? response.data : response.data[0];
        localStorage.setItem("userData", JSON.stringify(userData));
        successNotify(response?.message);

        setTimeout(() => {
          if (path === "/admin") {
            navigate("/matrimony");
          } else {
            const percentage = userData?.profileCompletionPercentage;
            percentage >= 80
              ? navigate(`/profile/${userData?._id}`)
              : navigate("/editprofile");
          }
        }, 1500);

      } else {
        errorNotify(response?.message || "Invalid login credentials.");
      }
    } catch (err) {
      errorNotify("An unexpected error occurred.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="font-jost login-right-container flex min-h-screen w-full items-center justify-center pt-16">
      <div className="mx-auto mb-5 flex w-[95%] flex-col items-center justify-center shadow-md lg:h-[400px] lg:w-[70%] lg:flex-row">

        {/* Left Section */}
        <div className="login-left-container relative h-[250px] w-full rounded-tl-lg rounded-tr-lg bg-loginbg lg:h-[500px] lg:w-[35%] lg:rounded-bl-lg lg:rounded-tl-lg lg:p-6">
          <div className="playFair flex flex-col space-y-2 p-4">
            <h3 className="text-center text-3xl text-textGreen lg:text-left lg:text-2xl">{Strings.NOW}</h3>
            <h2 className="text-center text-4xl text-textGreen lg:text-left lg:text-6xl">{Strings.LOGIN_TEXT}</h2>
            <h3 className="text-center text-2xl text-textGreen lg:text-left lg:text-2xl">{Strings.EASY_FAST}</h3>
          </div>
          <div className="imgBg absolute bottom-0 left-0 right-0"></div>
        </div>

        {/* Right Section */}
        <div className="flex h-auto w-full flex-col rounded-bl-lg rounded-br-lg bg-bgWhite lg:h-[500px] lg:w-[65%] lg:px-12">
          <div className="mt-6 flex flex-col space-y-4 p-4">
            <h3 className="playFair text-left text-2xl text-textBlack">{Strings.LOGIN_CONTEN}</h3>
            <p className="pb-6 text-left text-xs font-medium tracking-wider text-textGreen">
              {Strings.Do_Want}
              <span className="text-sky-500 hover:underline">
                <Link to="/register"> {Strings.SIGN_UP}</Link>
              </span>
            </p>
            <hr />
          </div>

          <form
            onSubmit={formik.handleSubmit}
            autoComplete="off"
            className="flex w-full flex-col gap-4 px-2 lg:mt-8"
          >
            {/* Email */}
            <div className="flex flex-col gap-1">
              <label htmlFor="primaryEmail" className="font-helvetica text-sm text-textBlack">
                {Strings.EMAIL}
              </label>
              <input
                id="primaryEmail"
                name="primaryEmail"
                type="email"
                autoComplete="off"
                placeholder="Enter your Email"
                className="text-md rounded-lg border border-gray-200 p-2.5 text-textBlack"
                onChange={formik.handleChange}
                value={formik.values.primaryEmail}
                onBlur={formik.handleBlur}
              />
              {formik.touched.primaryEmail && formik.errors.primaryEmail && (
                <span className="text-xs text-red-500">{formik.errors.primaryEmail}</span>
              )}
            </div>

            {/* Password */}
            <div className="flex flex-col gap-1">
              <label htmlFor="password" className="font-helvetica text-sm text-textBlack">
                {Strings.PASSWORD}
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Enter your password"
                  className="text-md w-full rounded-lg border border-gray-200 p-2.5 pr-10 text-textBlack focus:outline-none"
                  onChange={formik.handleChange}
                  value={formik.values.password}
                  onBlur={formik.handleBlur}
                />
                <div
                  className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-textBlack"
                  onClick={handleShowPassword}
                >
                  {showPassword ? <HiEyeOff /> : <HiEye />}
                </div>
              </div>
              {formik.touched.password && formik.errors.password && (
                <span className="text-xs text-red-500">{formik.errors.password}</span>
              )}
            </div>

            {/* Admin + Remember Me + Forget Password */}
            <div className="flex items-center justify-between px-1">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-xs text-sky-500 hover:underline">
                  <RiAdminFill />
                  <Link to={path === "/admin" ? "/login" : "/admin"}>
                    {path === "/admin" ? "User Login" : "Admin Login"}
                  </Link>
                </div>

                {/* Remember Me */}

              </div>

              <p
                className="cursor-pointer text-sm text-red-500 hover:underline"
                onClick={handleForgetPassword}
              >
                {Strings.FORGET_PASSWORD}
              </p>
            </div>
                            <div className="mt-1 flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="rememberMe"
                    className="h-4 w-4 cursor-pointer accent-textPink"
                    checked={rememberMe}
                    onChange={handleRememberMeChange}
                  />
                  <label htmlFor="rememberMe" className="cursor-pointer text-sm text-textBlack">
                    Remember Me
                  </label>
                </div>

            {/* Submit */}
            <button
              type="submit"
              className="font-helvetica mx-auto mt-2 flex w-full items-center justify-center rounded-lg bg-textPink p-2 text-sm text-bgWhite"
              disabled={isProcessing}
            >
              {isProcessing ? <Spinner /> : "Login"}
            </button>
          </form>
        </div>

        {forgetPasswordPopup && (
          <ForgetPassword
            setForgetPasswordPopup={setForgetPasswordPopup}
            primaryEmail={formik.values.primaryEmail}
            isPasswordExpired={isPasswordExpired}
          />
        )}
        
        <MessageBar />
      </div>
    </div>
  );
};

export default Login;